import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md text-sm font-semibold transition-colors duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-gray-200 text-gray-600 hover:bg-gray-300",
        destructive: "bg-danger text-white hover:bg-danger-active",
        outline:
          "border border-gray-300 bg-white text-gray-600 hover:bg-gray-100",
        secondary:
          "bg-primary-light text-primary-active hover:bg-primary hover:text-white",
        secondaryDestructive:
          "bg-danger-light text-danger-active hover:bg-danger hover:text-white",
        ghost: "hover:bg-gray-200/50",
        link: "text-primary underline-offset-4 hover:underline",
        primary: "bg-primary text-white hover:bg-primary-active",
        card: "border border-gray-200 bg-white text-gray-700 shadow-[0_3px_4px_0_rgba(0,0,0,0.03)] hover:border-white hover:bg-gray-200",
        primaryCard:
          "border border-white/30 bg-primary text-white shadow-[0_3px_4px_0_rgba(0,0,0,0.03)] hover:border-white/50 hover:bg-primary-active",
        successCard:
          "border border-white/30 bg-success-active text-white shadow-[0_3px_4px_0_rgba(0,0,0,0.03)] hover:bg-success-active/60",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "rounded-md px-3 py-1.5 text-xs",
        lg: "rounded-md px-8 py-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    readonly asChild?: boolean;
  };

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}
